import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {SnackBarContext} from '../contexts/SnackBarContext/SnackBarContext';
import {QuestionnaireCategoryItems} from '../features/company/culture/surveys/types/SurveyQuestionnaireTypes';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {
  AnswersRequestBody,
  BenefitsRequestBody,
  CompleteQuestionnaireRequestBody,
  DemographicsRequestBody,
  FeedbackRequestBody,
  GenerateQuestionnaireRequestBody,
  StrengthsRequestBody,
} from '../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {ApiResponseError} from '../lib/api/v1/types';
import {QuestionnaireSection} from '../types/CompanyQuestionnaire';

import {useApiRead} from './useApiRead';
import {useApiWrite} from './useApiWrite';
import {useGenerateNewQuestionnaire} from './useGenerateNewQuestionnaire';

export type QuestionnaireSubmit = <T extends QuestionnaireSection>(section: T, body: RequestBody[T]) => void;

type Method = 'post' | 'put' | 'patch';

type RequestBody = {
  [QuestionnaireSection.Demographics]: DemographicsRequestBody;
  [QuestionnaireSection.CulturalQuestions]: AnswersRequestBody;
  [QuestionnaireSection.DreamEmployerAnswers]: AnswersRequestBody;
  [QuestionnaireSection.HappinessAnswers]: AnswersRequestBody;
  [QuestionnaireSection.Benefits]: BenefitsRequestBody;
  [QuestionnaireSection.Strengths]: StrengthsRequestBody;
  [QuestionnaireSection.Feedback]: FeedbackRequestBody;
  [QuestionnaireSection.Complete]: CompleteQuestionnaireRequestBody;
};

const METHOD_TYPE: Record<QuestionnaireSection, Method> = {
  [QuestionnaireSection.Demographics]: 'put',
  [QuestionnaireSection.CulturalQuestions]: 'patch',
  [QuestionnaireSection.DreamEmployerAnswers]: 'patch',
  [QuestionnaireSection.HappinessAnswers]: 'patch',
  [QuestionnaireSection.Benefits]: 'patch',
  [QuestionnaireSection.Strengths]: 'post',
  [QuestionnaireSection.Feedback]: 'post',
  [QuestionnaireSection.Complete]: 'patch',
};

// Order of sections
const STEP: Record<QuestionnaireSection, number> = {
  [QuestionnaireSection.Demographics]: 0,
  [QuestionnaireSection.CulturalQuestions]: 1,
  [QuestionnaireSection.DreamEmployerAnswers]: 2,
  [QuestionnaireSection.Benefits]: 3,
  [QuestionnaireSection.Strengths]: 4,
  [QuestionnaireSection.HappinessAnswers]: 5,
  [QuestionnaireSection.Feedback]: 6,
  [QuestionnaireSection.Complete]: 7,
};

const sectionKeys = Object.keys(STEP) as QuestionnaireSection[];
const EMPTY_OMITTED_SECTIONS: QuestionnaireSection[] = [];

interface Args {
  questionnairePayload?: GenerateQuestionnaireRequestBody;
  onUpdateSuccess?: (data?: any) => void;
  omittedSections?: QuestionnaireSection[];
}

export const useCompanyQuestionnaireUpdate = ({
  questionnairePayload,
  omittedSections = EMPTY_OMITTED_SECTIONS,
}: Args) => {
  const {
    questionnaire,
    isLoading: isQuestionnaireLoading,
    error: questionnaireError,
  } = useGenerateNewQuestionnaire(questionnairePayload);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();

  const [step, setCurrentStep] = useState<number>(STEP[QuestionnaireSection.Demographics]);
  const [fetchItems, setFetchItems] = useState(false);
  const [culturalItemsOverride, setCulturalItemsOverride] = useState<QuestionnaireCategoryItems>();

  const currentSection = sectionKeys[step];

  const goNext = useCallback(() => {
    if (step >= 7) return;

    let nextStep = step + 1;

    // Skips to step 3 if no dreamEmployers are present.
    while (
      nextStep < sectionKeys.length &&
      ((!questionnaire?.dreamEmployerItems && sectionKeys[nextStep] === QuestionnaireSection.DreamEmployerAnswers) ||
        omittedSections.includes(sectionKeys[nextStep]))
    ) {
      nextStep = nextStep + 1;
    }

    setCurrentStep(nextStep);
  }, [step, questionnaire?.dreamEmployerItems, omittedSections]);

  const handleError = (error?: ApiResponseError) => {
    if (error && currentSection) {
      const sectionName = currentSection.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
      const errorMessage = error.extra_details || 'An unexpected error occurred';

      dispatchMessage(`Error in ${sectionName} - ${t('shared.errors.takeScreenShotError')}: ${errorMessage}`, 'error');
    }
  };

  const onSectionSuccess = useCallback(() => {
    if (currentSection === QuestionnaireSection.Demographics) {
      setFetchItems(true);
    } else {
      goNext();
    }
  }, [currentSection, goNext]);

  const {makeManualRequestWithParams, isLoading} = useApiWrite({onSuccess: onSectionSuccess, onError: handleError});

  const {
    data: surveyItems,
    isLoading: isFetchingItems,
    error: surveyItemsError,
  } = useApiRead<QuestionnaireCategoryItems>({
    path: GetRoutes.getCompanySurveyItems(questionnaire?.resourceId || ''),
    options: {enabled: fetchItems && !!questionnaire?.resourceId, retry: false},
  });

  useEffect(() => {
    if (!fetchItems || isFetchingItems) return;
    if (surveyItems) setCulturalItemsOverride(surveyItems);
    setFetchItems(false);
    goNext();
  }, [surveyItems, surveyItemsError, fetchItems, isFetchingItems, goNext]);

  const submitSection: QuestionnaireSubmit = <T extends QuestionnaireSection>(section: T, body: RequestBody[T]) => {
    if (!questionnaire?.resourceId) return;

    const urlSection = section !== QuestionnaireSection.Complete ? `/${section}` : '';
    const path = `/api/v1/public/company_survey_questionnaires/${questionnaire.resourceId}${urlSection}`;
    const method = METHOD_TYPE[section];

    makeManualRequestWithParams({method, path, body});
  };

  const questions = useMemo(() => {
    const cultureSource = culturalItemsOverride ?? questionnaire?.culturalCategoriesItems;
    return {
      culture: cultureSource?.questions.map((q) => q.items) || [],
      dreamEmployer: questionnaire?.dreamEmployerItems?.questions.map((q) => q.items) || [],
      happiness: questionnaire?.happinessItems?.questions?.map((q) => q.items) ?? [],
      existingBenefits: questionnaire?.existingBenefits?.questions?.map((b) => b) ?? [],
      desiredBenefits: questionnaire?.desiredBenefits?.questions?.map((b) => b) ?? [],
      strengths: questionnaire?.companyStrengthsItems?.questions?.map((s) => s.items).flat() ?? [],
    };
  }, [questionnaire, culturalItemsOverride]);

  useEffect(() => {
    if (step === STEP[QuestionnaireSection.Complete]) {
      const payload: CompleteQuestionnaireRequestBody = {
        companySurveyQuestionnaire: {
          completedAt: new Date().toISOString(),
        },
      };

      submitSection(QuestionnaireSection.Complete, payload);
    }
    //eslint-disable-next-line
  }, [step]);

  return {
    isQuestionnaireLoading,
    questionnaireError,
    isLoading: isLoading || isFetchingItems,
    questionnaire,
    questions,
    submitSection,
    currentStep: sectionKeys[step],
    goNext,
  };
};
